import {Injectable} from '@angular/core';
import {Observable, Subject, take} from 'rxjs';

/**
 * Queues HTTP requests that fail with 401 while a token refresh is in progress.
 * When refresh succeeds, queued requests are automatically retried.
 * When refresh fails, queued requests are silently abandoned (logout handles navigation).
 */
@Injectable({providedIn: 'root'})
export class TokenRefreshQueue {
  private refreshInProgress = false;
  private refreshComplete$ = new Subject<void>();

  get isRefreshing(): boolean {
    return this.refreshInProgress;
  }

  startRefresh(): void {
    this.refreshInProgress = true;
  }

  completeRefresh(): void {
    this.refreshInProgress = false;
    this.refreshComplete$.next();
  }

  failRefresh(): void {
    this.refreshInProgress = false;
    this.refreshComplete$.error(new Error('Token refresh failed'));
    // Reset the Subject so future waits work after a failed refresh
    this.refreshComplete$ = new Subject<void>();
  }

  /** Clear any pending refresh state (called on explicit logout) */
  clear(): void {
    if (this.refreshInProgress) {
      this.refreshInProgress = false;
      this.refreshComplete$.error(new Error('Refresh cancelled by logout'));
      this.refreshComplete$ = new Subject<void>();
    }
  }

  /** Observable that emits once when refresh completes */
  waitForRefresh(): Observable<void> {
    return this.refreshComplete$.asObservable().pipe(take(1));
  }
}
